/**
 * Quest tracker — a small side block listing the first few quests the player
 * holds, each with the one thing left to do.
 *
 * Driven by the same store the Journal reads, so it redraws when a quest is
 * accepted in conversation, handed in, or claimed, with no fetch of its own.
 * Desktop only: below the HUD's phone breakpoint it is hidden by CSS (the block
 * would sit over the playfield on a small screen), so there is nothing to
 * branch on here. Clicking it opens the Journal.
 */
import { questStateStore } from '../../state/quest-state';
import { TRACKER_ROWS, trackedQuests, type TrackedQuest } from '../../state/quest-tracker';

export interface QuestTrackerUI {
  readonly el: HTMLElement;
  dispose(): void;
}

function row(q: TrackedQuest): HTMLElement {
  const li = document.createElement('li');
  li.className = 'bv-qt-row';
  li.dataset.source = q.source;
  if (q.ready) li.classList.add('bv-qt-ready');

  const name = document.createElement('span');
  name.className = 'bv-qt-name';
  name.textContent = q.name;

  const task = document.createElement('span');
  task.className = 'bv-qt-task';
  task.textContent = q.task;

  li.append(name, task);
  if (q.count) {
    const count = document.createElement('span');
    count.className = 'bv-qt-count';
    count.textContent = q.count;
    li.appendChild(count);
  }
  const bar = document.createElement('span');
  bar.className = 'bv-qt-bar';
  const fill = document.createElement('span');
  fill.className = 'bv-qt-fill';
  fill.style.width = `${Math.round(q.fraction * 100)}%`;
  bar.appendChild(fill);
  li.appendChild(bar);
  return li;
}

export function createQuestTrackerUI(parent: HTMLElement, openJournal: () => void): QuestTrackerUI {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = 'bv-qt';
  el.hidden = true;
  el.setAttribute('aria-label', 'Active quests — open the journal');
  el.addEventListener('click', openJournal);

  const head = document.createElement('span');
  head.className = 'bv-qt-head';
  const title = document.createElement('span');
  title.textContent = 'Active quests';
  const total = document.createElement('span');
  total.className = 'bv-qt-total';
  head.append(title, total);

  const list = document.createElement('ul');
  list.className = 'bv-qt-list';
  const more = document.createElement('span');
  more.className = 'bv-qt-more';
  el.append(head, list, more);
  parent.appendChild(el);

  const render = (): void => {
    const all = trackedQuests(questStateStore.get());
    el.hidden = all.length === 0;
    total.textContent = String(all.length);
    list.replaceChildren(...all.slice(0, TRACKER_ROWS).map(row));
    const rest = all.length - TRACKER_ROWS;
    more.textContent = rest > 0 ? `+${rest} more in the journal` : '';
    more.hidden = rest <= 0;
  };
  render();
  const unsub = questStateStore.subscribe(render);

  return {
    el,
    dispose(): void {
      unsub();
      el.remove();
    },
  };
}
